import { fromLocal, parseDue, toLocal } from "@letsdoit/due-parser";
import type { DateOp, DateValue } from "./ast";

/** A local wall-clock point: day number (days since 1970-01-01) + minutes since midnight. */
export interface LocalPoint {
  dn: number;
  minutes: number;
}

/** Half-open local interval; a missing side is unbounded. */
export interface LocalInterval {
  from?: LocalPoint;
  to?: LocalPoint;
}

export interface DateContext {
  now: Date;
  /** The user's IANA timezone; every relative date is resolved in it. */
  timezone: string;
  /** `start_day` / `next_week` user settings (ISO weekdays). */
  startDay?: number;
  nextWeek?: number;
}

const UNITS: Record<string, Extract<DateValue, { kind: "offset" }>["unit"]> = {
  m: "minute",
  min: "minute",
  mins: "minute",
  minute: "minute",
  minutes: "minute",
  分: "minute",
  分钟: "minute",
  h: "hour",
  hr: "hour",
  hrs: "hour",
  hour: "hour",
  hours: "hour",
  小时: "hour",
  个小时: "hour",
  d: "day",
  day: "day",
  days: "day",
  天: "day",
  日: "day",
  w: "week",
  week: "week",
  weeks: "week",
  周: "week",
  星期: "week",
  个星期: "week",
  个周: "week",
  month: "month",
  months: "month",
  月: "month",
  个月: "month",
  y: "year",
  year: "year",
  years: "year",
  年: "year",
};

const OFFSET_RE = /^([+-])\s*(\d{1,5})\s*([a-z]+|个?小时|分钟?|天|日|个?周|个?星期|个?月|年)$/;
const NEXT_DAYS_RE = /^(?:next\s+)?(\d{1,4})\s*days?$|^(?:未来|接下来|今后|之后)?(\d{1,4})\s*天$/;
const PAST_DAYS_RE = /^(?:-\s*(\d{1,4})\s*days?|(?:past|last)\s+(\d{1,4})\s*days?)$|^(?:-\s*|过去)(\d{1,4})\s*天$/;

/** Fixed instant for syntax validation; real resolution happens with the caller's `now`. */
const VALIDATION_NOW = new Date("2026-01-15T12:00:00Z");

/**
 * Parses the value of a date operator or a bare date term. Ranges and offsets are handled here;
 * everything else must be a one-off date understood by `@letsdoit/due-parser` (recurring
 * strings such as "every day" are not dates). Returns null when it is not a date.
 */
export function parseDateValue(text: string, lang?: string, bare = false): DateValue | null {
  const t = text.trim().toLowerCase().replace(/\s+/g, " ");
  if (!t) return null;
  // A bare `7 days` / `-3 days` is a range; after an operator (`created before: -365 days`)
  // it is the single day that many days away.
  const past = PAST_DAYS_RE.exec(t);
  const pastDays = past ? Number(past[1] ?? past[2] ?? past[3]) : undefined;
  const next = NEXT_DAYS_RE.exec(t);
  const nextDays = next ? Number(next[1] ?? next[2]) : undefined;
  if (bare && pastDays !== undefined) return { kind: "days", days: -pastDays };
  if (bare && nextDays !== undefined) return { kind: "days", days: nextDays };
  if (!bare && pastDays !== undefined) return { kind: "offset", amount: -pastDays, unit: "day" };
  if (!bare && nextDays !== undefined) return { kind: "offset", amount: nextDays, unit: "day" };
  const off = OFFSET_RE.exec(t);
  if (off) {
    const unit = UNITS[off[3] as string];
    if (unit) return { kind: "offset", amount: (off[1] === "-" ? -1 : 1) * Number(off[2]), unit };
  }
  const r = parseDue({ string: text.trim(), lang: lang ?? null }, { now: VALIDATION_NOW, userTimezone: "UTC" });
  if (!r.ok || r.due === null || r.due.is_recurring) return null;
  return lang ? { kind: "expr", text: text.trim(), lang } : { kind: "expr", text: text.trim() };
}

const MS_DAY = 86_400_000;

function dayOf(date: string): number {
  return Math.round(
    Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10))) / MS_DAY,
  );
}

const pad = (n: number) => String(n).padStart(2, "0");

/** `YYYY-MM-DD` of a day number. */
export function formatDay(dn: number): string {
  return new Date(dn * MS_DAY).toISOString().slice(0, 10);
}

/** `YYYY-MM-DDTHH:MM:SS` local string of a point. */
export function formatLocal(p: LocalPoint): string {
  return `${formatDay(p.dn)}T${pad(Math.floor(p.minutes / 60))}:${pad(p.minutes % 60)}:00`;
}

/** The point as a UTC instant `YYYY-MM-DDTHH:MM:SSZ`, interpreting it in `timezone`. */
export function pointToUtc(p: LocalPoint, timezone: string): string {
  return fromLocal(formatLocal(p), timezone)
    .toISOString()
    .replace(/\.\d{3}Z$/, "Z");
}

function normalize(p: LocalPoint): LocalPoint {
  const dn = p.dn + Math.floor(p.minutes / 1440);
  return { dn, minutes: ((p.minutes % 1440) + 1440) % 1440 };
}

/** The current local point and today in `ctx.timezone`. */
export function localNow(ctx: DateContext): LocalPoint {
  const local = toLocal(ctx.now, ctx.timezone);
  return { dn: dayOf(local), minutes: Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16)) };
}

function addMonths(dn: number, months: number): number {
  const d = new Date(dn * MS_DAY);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + months;
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return Math.round(Date.UTC(y, m, Math.min(d.getUTCDate(), last)) / MS_DAY);
}

/**
 * What a date value covers: a whole-day range (`instant: false`) or a single instant.
 * Throws when an `expr` no longer parses (it was validated at parse time, so this is a bug).
 */
export function resolveDateValue(
  value: DateValue,
  ctx: DateContext,
): { from: LocalPoint; to: LocalPoint; instant: boolean } {
  const now = localNow(ctx);
  const day = (dn: number) => ({ from: { dn, minutes: 0 }, to: { dn: dn + 1, minutes: 0 }, instant: false });
  switch (value.kind) {
    case "days":
      return value.days >= 0
        ? { from: { dn: now.dn, minutes: 0 }, to: { dn: now.dn + value.days, minutes: 0 }, instant: false }
        : { from: { dn: now.dn + value.days, minutes: 0 }, to: { dn: now.dn, minutes: 0 }, instant: false };
    case "offset": {
      const n = value.amount;
      if (value.unit === "minute" || value.unit === "hour") {
        const p = normalize({ dn: now.dn, minutes: now.minutes + n * (value.unit === "hour" ? 60 : 1) });
        return { from: p, to: p, instant: true };
      }
      if (value.unit === "day") return day(now.dn + n);
      if (value.unit === "week") return day(now.dn + 7 * n);
      return day(addMonths(now.dn, value.unit === "month" ? n : 12 * n));
    }
    case "expr": {
      const r = parseDue(
        { string: value.text, lang: value.lang ?? null },
        { now: ctx.now, userTimezone: ctx.timezone, startDay: ctx.startDay, nextWeek: ctx.nextWeek },
      );
      if (!r.ok || !r.due) throw new Error(`Unresolvable date: ${value.text}`);
      const date = r.due.date;
      if (date.length === 10) return day(dayOf(date));
      // Floating local time (no explicit timezone is ever passed, so never fixed).
      const p = { dn: dayOf(date), minutes: Number(date.slice(11, 13)) * 60 + Number(date.slice(14, 16)) };
      return { from: p, to: p, instant: true };
    }
  }
}

/**
 * The local interval a date operator selects:
 * - `on`: the value's days (an instant means its whole day);
 * - `before`: everything before the value's start (an instant: strictly before it);
 * - `after`: everything after the value's end (an instant: strictly after it).
 */
export function dateOpInterval(op: DateOp, value: DateValue, ctx: DateContext): LocalInterval {
  const r = resolveDateValue(value, ctx);
  if (op === "before") return { to: r.from };
  if (op === "after") return { from: r.instant ? normalize({ dn: r.to.dn, minutes: r.to.minutes + 1 }) : r.to };
  if (r.instant) return { from: { dn: r.from.dn, minutes: 0 }, to: { dn: r.from.dn + 1, minutes: 0 } };
  return { from: r.from, to: r.to };
}

/** `overdue`: before now (full-day dates: before today). */
export function overdueInterval(ctx: DateContext): LocalInterval {
  return { to: localNow(ctx) };
}

/**
 * The strings a store needs to test a due date against an interval, for each of the three due
 * shapes (guides/Due_dates.md):
 * - full-day `YYYY-MM-DD`: `fromDay <= date < toDay` (a full day counts only when the interval
 *   covers it from midnight: a bound with a time rounds the day out);
 * - floating `YYYY-MM-DDTHH:MM:SS`: `fromLocal <= date < toLocal` (user's wall clock);
 * - fixed (UTC) `…Z` and timestamps like `added_at`: `fromUtc <= date < toUtc`.
 */
export interface IntervalBounds {
  fromDay?: string;
  toDay?: string;
  fromLocal?: string;
  toLocal?: string;
  fromUtc?: string;
  toUtc?: string;
}

export function intervalBounds(interval: LocalInterval, timezone: string): IntervalBounds {
  const out: IntervalBounds = {};
  const { from, to } = interval;
  if (from) {
    out.fromDay = formatDay(from.minutes > 0 ? from.dn + 1 : from.dn);
    out.fromLocal = formatLocal(from);
    out.fromUtc = pointToUtc(from, timezone);
  }
  if (to) {
    out.toDay = formatDay(to.dn);
    out.toLocal = formatLocal(to);
    out.toUtc = pointToUtc(to, timezone);
  }
  return out;
}

/** In-memory check of a stored due/deadline/timestamp string against interval bounds. */
export function dateInBounds(date: string, b: IntervalBounds): boolean {
  let lo: string | undefined;
  let hi: string | undefined;
  let v = date;
  if (date.length === 10) {
    lo = b.fromDay;
    hi = b.toDay;
  } else if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(date)) {
    v = new Date(date)
      .toISOString()
      .replace(/\.\d{3}Z$/, "Z")
      .slice(0, 19);
    lo = b.fromUtc?.slice(0, 19);
    hi = b.toUtc?.slice(0, 19);
  } else {
    v = date.slice(0, 19);
    lo = b.fromLocal;
    hi = b.toLocal;
  }
  return (lo === undefined || v >= lo) && (hi === undefined || v < hi);
}
